// Encryption and permissions for exports.
//
// The setting is per document and sticky for the session: set it once
// and every download applies it. It is deliberately NOT persisted to the
// IndexedDB session — the same rule the open-password follows, because a
// password sitting in browser storage is worse than retyping one.
import { useEffect, useState } from "react";
import type { DocPermissions, SecurityOptions } from "../lib/protocol.ts";
import {
  ALL_ALLOWED,
  PERMISSION_LABELS,
  PERMISSION_ORDER,
} from "../lib/doc-security.ts";

export function SecurityDialog({
  value,
  sourceEncrypted,
  busy,
  onCancel,
  onApply,
}: {
  value: SecurityOptions;
  /** The open document is itself password-protected. */
  sourceEncrypted: boolean;
  busy: boolean;
  onCancel: () => void;
  /** `download` runs the export immediately after storing the setting. */
  onApply: (next: SecurityOptions, download: boolean) => void;
}) {
  const [mode, setMode] = useState<SecurityOptions["mode"]>(value.mode);
  const [userPw, setUserPw] = useState(value.userPassword ?? "");
  const [userPw2, setUserPw2] = useState(value.userPassword ?? "");
  const [ownerPw, setOwnerPw] = useState(value.ownerPassword ?? "");
  const [perms, setPerms] = useState<DocPermissions>(value.permissions ?? ALL_ALLOWED);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const mismatch = mode === "encrypt" && userPw !== userPw2;
  const noPassword = mode === "encrypt" && !userPw && !ownerPw;
  const invalid = mismatch || noPassword;

  const submit = (download: boolean) => {
    if (invalid || busy) return;
    onApply(
      mode === "encrypt"
        ? { mode, userPassword: userPw, ownerPassword: ownerPw, permissions: perms }
        : { mode },
      download
    );
  };

  const radio = (m: SecurityOptions["mode"], label: string, hint: string) => (
    <label className="flex cursor-pointer gap-2 rounded-md border border-[color:var(--color-border)] p-2 hover:bg-[color:var(--color-bg-subtle)]">
      <input
        type="radio"
        name="pe-security-mode"
        checked={mode === m}
        onChange={() => setMode(m)}
        className="mt-0.5"
      />
      <span className="min-w-0">
        <span className="block text-xs font-medium">{label}</span>
        <span className="block text-[11px] text-[color:var(--color-fg-muted)]">{hint}</span>
      </span>
    </label>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Security and permissions"
      className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(true);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        className="max-h-full w-full max-w-md space-y-3 overflow-y-auto rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl"
      >
        <div className="flex items-center gap-2">
          <i className="ri-shield-keyhole-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
          <h2 className="text-base font-semibold">Security &amp; permissions</h2>
        </div>

        <div className="space-y-1.5">
          {radio(
            "keep",
            sourceEncrypted ? "Keep the original protection" : "No protection",
            sourceEncrypted
              ? "Exports stay encrypted with the password this file already had."
              : "Exports are plain, unencrypted PDFs."
          )}
          {sourceEncrypted &&
            radio(
              "none",
              "Remove protection",
              "Exports open without a password. Only do this for a copy you're allowed to share."
            )}
          {radio(
            "encrypt",
            "Protect with a password",
            "AES-256. Choose an open password, a permissions password, or both."
          )}
        </div>

        {mode === "encrypt" && (
          <div className="space-y-3 rounded-md border border-[color:var(--color-border)] p-3">
            <div className="space-y-1.5">
              <label className="block text-[11px] font-medium text-[color:var(--color-fg-muted)]">
                Password to open{" "}
                <span className="font-normal">— leave blank to let anyone open it</span>
              </label>
              <input
                type="password"
                autoFocus
                value={userPw}
                onChange={(e) => setUserPw(e.target.value)}
                placeholder="Open password"
                className="h-8 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-xs"
              />
              <input
                type="password"
                value={userPw2}
                onChange={(e) => setUserPw2(e.target.value)}
                placeholder="Confirm open password"
                className="h-8 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-xs"
              />
              {mismatch && <p className="text-[11px] text-red-600">Passwords don&rsquo;t match.</p>}
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-medium text-[color:var(--color-fg-muted)]">
                Permissions password{" "}
                <span className="font-normal">— needed to lift the restrictions below</span>
              </label>
              <input
                type="password"
                value={ownerPw}
                onChange={(e) => setOwnerPw(e.target.value)}
                placeholder="Permissions password (optional)"
                className="h-8 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-xs"
              />
            </div>

            <fieldset className="space-y-1">
              <legend className="mb-1 text-[11px] font-medium text-[color:var(--color-fg-muted)]">
                Allow
              </legend>
              {PERMISSION_ORDER.map((k) => (
                <label key={k} className="flex items-center gap-2 text-[11px]">
                  <input
                    type="checkbox"
                    checked={perms[k]}
                    // High-resolution printing is meaningless with
                    // printing off — the PDF flag pair works that way.
                    disabled={k === "printHq" && !perms.print}
                    onChange={(e) =>
                      setPerms((p) => ({
                        ...p,
                        [k]: e.target.checked,
                        ...(k === "print" && !e.target.checked ? { printHq: false } : null),
                      }))
                    }
                  />
                  {PERMISSION_LABELS[k]}
                </label>
              ))}
            </fieldset>

            <p className="text-[11px] text-[color:var(--color-fg-muted)]">
              Restrictions are a request: standards-compliant viewers honour them, but they are not
              a guarantee. A password to open is the part that actually protects the file — and it{" "}
              <span className="font-medium">cannot be recovered</span> if you lose it.
            </p>
            {noPassword && (
              <p className="text-[11px] text-red-600">Set at least one password to encrypt.</p>
            )}
          </div>
        )}

        <p className="text-[11px] text-[color:var(--color-fg-muted)]">
          The copy open in this editor is unaffected — this applies to what you download. The
          setting is kept for this session only.
        </p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={invalid || busy}
            onClick={() => submit(false)}
            className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-50"
          >
            Apply
          </button>
          <button
            type="submit"
            disabled={invalid || busy}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-50"
          >
            <i className="ri-download-2-line" aria-hidden /> Apply &amp; download
          </button>
        </div>
      </form>
    </div>
  );
}
